import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Progressive Focus Mode Store
 *
 * Supports a hierarchical narrowing of context:
 *   Cycle → Track → Session → Tool
 *
 * Entering a narrower mode does not clear the wider one.
 * Exiting a wider mode clears all narrower ones (e.g. exitWorkspace also
 * clears track, session, and tool).
 *
 * Any mode being active drives:
 *   - Page-wide background tint (light slate/blue)
 *   - Sidebar mode indicators with clear (×) controls
 *   - Execution page filtering to the most-specific active context
 */

interface PinnedCycleState {
  // ── Cycle mode ──────────────────────────────────────────────────────────
  pinnedCycleId: string | null;
  pinnedCycleName: string | null;
  enterWorkspace: (id: string, name: string) => void;
  exitWorkspace: () => void;
  /** Legacy setter — kept for backward compat; sets id only. */
  setPinnedCycleId: (id: string | null) => void;

  // ── Track mode ───────────────────────────────────────────────────────────
  pinnedTrackId: string | null;
  pinnedTrackName: string | null;
  /** Entering a track also pins its parent cycle. */
  enterTrack: (id: string, name: string, cycleId: string, cycleName: string) => void;
  exitTrack: () => void;

  // ── Session mode ─────────────────────────────────────────────────────────
  pinnedSessionId: string | null;
  pinnedSessionName: string | null;
  /** Entering a session also pins its parent track and cycle. */
  enterSession: (id: string, name: string, trackId: string, trackName: string, cycleId: string, cycleName: string) => void;
  exitSession: () => void;

  // ── Tool mode ────────────────────────────────────────────────────────────
  pinnedToolId: string | null;
  pinnedToolName: string | null;
  /** Entering a tool also pins its parent session, track, and cycle. */
  enterTool: (id: string, name: string, sessionId: string, sessionName: string, trackId: string, trackName: string, cycleId: string, cycleName: string) => void;
  exitTool: () => void;

  // ── Derived helpers ──────────────────────────────────────────────────────
  /** True when any focus mode is active — drives page bg tint. */
  isInFocusMode: () => boolean;
  /** Clear all focus modes at once. */
  exitAll: () => void;
}

export const usePinnedCycleStore = create<PinnedCycleState>()(
  persist(
    (set, get) => ({
      // ── Cycle ──────────────────────────────────────────────────────────
      pinnedCycleId: null,
      pinnedCycleName: null,
      enterWorkspace: (id, name) =>
        // Entering a new cycle clears narrower modes
        set({ pinnedCycleId: id, pinnedCycleName: name, pinnedTrackId: null, pinnedTrackName: null, pinnedSessionId: null, pinnedSessionName: null, pinnedToolId: null, pinnedToolName: null }),
      exitWorkspace: () =>
        set({ pinnedCycleId: null, pinnedCycleName: null, pinnedTrackId: null, pinnedTrackName: null, pinnedSessionId: null, pinnedSessionName: null, pinnedToolId: null, pinnedToolName: null }),
      setPinnedCycleId: (id) =>
        set({ pinnedCycleId: id, pinnedCycleName: null }),

      // ── Track ───────────────────────────────────────────────────────────
      pinnedTrackId: null,
      pinnedTrackName: null,
      enterTrack: (id, name, cycleId, cycleName) =>
        // Pins the track and its parent cycle; clears any narrower context
        set({
          pinnedCycleId: cycleId, pinnedCycleName: cycleName,
          pinnedTrackId: id, pinnedTrackName: name,
          pinnedSessionId: null, pinnedSessionName: null,
          pinnedToolId: null, pinnedToolName: null,
        }),
      exitTrack: () =>
        set({ pinnedTrackId: null, pinnedTrackName: null, pinnedSessionId: null, pinnedSessionName: null, pinnedToolId: null, pinnedToolName: null }),

      // ── Session ─────────────────────────────────────────────────────────
      pinnedSessionId: null,
      pinnedSessionName: null,
      enterSession: (id, name, trackId, trackName, cycleId, cycleName) =>
        // Pins the session and its parent track + cycle; clears tool
        set({
          pinnedCycleId: cycleId, pinnedCycleName: cycleName,
          pinnedTrackId: trackId, pinnedTrackName: trackName,
          pinnedSessionId: id, pinnedSessionName: name,
          pinnedToolId: null, pinnedToolName: null,
        }),
      exitSession: () =>
        set({ pinnedSessionId: null, pinnedSessionName: null, pinnedToolId: null, pinnedToolName: null }),

      // ── Tool ────────────────────────────────────────────────────────────
      pinnedToolId: null,
      pinnedToolName: null,
      enterTool: (id, name, sessionId, sessionName, trackId, trackName, cycleId, cycleName) =>
        // Pins the tool and its full ancestor chain
        set({
          pinnedCycleId: cycleId, pinnedCycleName: cycleName,
          pinnedTrackId: trackId, pinnedTrackName: trackName,
          pinnedSessionId: sessionId, pinnedSessionName: sessionName,
          pinnedToolId: id, pinnedToolName: name,
        }),
      exitTool: () => set({ pinnedToolId: null, pinnedToolName: null }),

      // ── Helpers ─────────────────────────────────────────────────────────
      isInFocusMode: () => {
        const { pinnedCycleId, pinnedTrackId, pinnedSessionId, pinnedToolId } = get();
        return !!(pinnedCycleId || pinnedTrackId || pinnedSessionId || pinnedToolId);
      },
      exitAll: () =>
        set({
          pinnedCycleId: null, pinnedCycleName: null,
          pinnedTrackId: null, pinnedTrackName: null,
          pinnedSessionId: null, pinnedSessionName: null,
          pinnedToolId: null, pinnedToolName: null,
        }),
    }),
    { name: 'priorities:pinned-cycle' }
  )
);
